/**
 * Lifecycle / pill mapping for the unified appointment-detail surface.
 *
 * Single source of truth referenced by CONTEXT D-05 (Phase 4.6). Every
 * downstream consumer (`AppointmentDetailView`, `PageTitleRow`,
 * `HospitalityCard`, `MagicLinkAppointmentView`, `StatusPill`) reads
 * lifecycle phase + pill key through this module rather than inlining
 * `status === 'cancelled'` checks at the call site.
 *
 * Two boundaries this module owns:
 *
 *   1. The `cancelled` / `canceled` spelling dual-map (legacy API quirk —
 *      Rails emits `canceled`, TypeScript canon is `cancelled`). Adapter
 *      normalises the wire spelling; this helper accepts both as a
 *      defensive belt-and-suspenders so a stray un-adapted call site
 *      still maps correctly.
 *
 *   2. The `quote_denied` + `lost_opportunity` collapse to `cancelled`.
 *      Legacy magic-link conflates these statuses into the cancelled
 *      surface; sketch 022 extends that conflation to the authenticated
 *      surface for visual parity (per CONTEXT D-05 footnote).
 *
 * Pill copy (`STATUS_PILL_COPY`) mirrors sketch 022's `S` configuration
 * at `.planning/sketches/022-appointment-detail/index.html:6436`. Edits
 * to copy live here, not at the render site.
 */

export type LifecyclePhase = 'active' | 'completed' | 'cancelled';

export type StatusPillKey =
  | 'pending'
  | 'assigned'
  | 'accepted'
  | 'enroute'
  | 'arrived'
  | 'late'
  | 'completed'
  | 'completed-unpaid'
  | 'cancelled'
  // 5.5 D-18 additions — quote-specific pill keys.
  // `pending` (above) is reused for pending + denied quotes (D-12).
  // `accepted` (above) is reused for accepted quotes; the QuoteStatusPill
  // anchors a different palette tone via `PILL_KEY_CLASSES` in StatusPill.vue.
  // `expired` is the only net-new key — gray/muted semantic per D-12.
  | 'expired'
  // 5.2 D-21 additions — invoice + plan-row (installment) status keys, mapped
  // by `utils/invoice-status.ts`. Extend (not fork) the shared union per the
  // 5.5 `expired` precedent; the mapping logic above is untouched.
  //   `paid`         — success / green   (paid_in_full / paid)
  //   `open`         — info / sky-blue   (open invoice)
  //   `partial`      — warning / amber   (partial / partially_paid)
  //   `current-draw` — warning / amber   (current_draw — fully-unpaid draw; 260618-esd)
  //   `past-due`     — danger / red      (past_due)
  //   `scheduled`    — info / sky-blue   (future plan rows)
  | 'paid'
  | 'open'
  | 'partial'
  | 'past-due'
  | 'scheduled'
  // 260618-esd: net-new current-draw plan-row key (warning/amber), distinct from
  // `partial`. A `current_draw` installment is fully unpaid (nothing paid yet),
  // so it must NOT inherit the "Partially Paid" copy/icon — its own pill below.
  | 'current-draw';

/** Statuses that collapse to the `'cancelled'` lifecycle phase. */
const CANCELLED_STATUSES: ReadonlySet<string> = new Set([
  'cancelled',
  'canceled',
  'quote_denied',
  'lost_opportunity',
]);

/** Statuses that are part of the active lifecycle. */
const ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'assigned',
  'preferred_assignment',
  'accepted',
  'running',
  'late',
]);

/**
 * Active-lifecycle pill projection driven by the appointment's own dispatch
 * progression (260526 — "map status like the consumer portal"):
 *
 *   arrival_time set                 → `arrived`  (tech on site)
 *   dispatch_time set OR status===running (no arrival) → `enroute`
 *   accepted                         → `accepted` (tech claimed, not started)
 *   assigned / preferred_assignment  → `assigned`
 *   pending / unknown                → `pending`
 *
 * Timestamps are APPOINTMENT-level (`iso_dispatch_time` / `iso_arrival_time`
 * via the adapter) — independent of the per-tech `technician_status`.
 * Extracted so {@link mapStatusToPillKey} stays under the complexity ceiling.
 */
function mapActivePillKey(
  status: string,
  dispatch_time?: string | null,
  arrival_time?: string | null,
): StatusPillKey {
  if (arrival_time) return 'arrived';
  if (dispatch_time || status === 'running') return 'enroute';
  if (status === 'accepted') return 'accepted';
  if (status === 'assigned' || status === 'preferred_assignment') {
    return 'assigned';
  }
  return 'pending';
}

/**
 * Map a raw API `status` string to its coarse lifecycle phase.
 *
 * The phase drives whole-card visibility (manage popover hidden on
 * terminal states; quote card hidden on cancelled; invoice card shown
 * on completed + cancelled-with-balance). Per-status pill copy lives on
 * {@link mapStatusToPillKey} + {@link STATUS_PILL_COPY}.
 *
 * Unknown statuses degrade to `'active'` so the surface still renders
 * rather than collapsing to the cancelled layout when the API ships a
 * new status the client hasn't learned yet.
 */
export function mapStatusToLifecycle(status: string): LifecyclePhase {
  const normalised = (status ?? '').toLowerCase();
  if (CANCELLED_STATUSES.has(normalised)) return 'cancelled';
  if (normalised === 'completed') return 'completed';
  if (ACTIVE_STATUSES.has(normalised)) return 'active';
  // Defensive default: unknown wire values keep the active layout so the
  // detail view continues to render. Never silently classify an unknown
  // status as cancelled — that would hide actions the customer may need.
  return 'active';
}

/**
 * Map a raw API `status` (plus invoice fields) to the granular pill key
 * driving the title-row status badge.
 *
 * The completed-paid vs completed-unpaid split is driven by
 * `balance_cents`: any positive balance means the customer still owes
 * money, regardless of whether `paid_at` was opportunistically set on
 * a partial-payment row. Treats `balance_cents` undefined as zero so
 * legacy payloads that omit the field render `'completed'` rather than
 * `'completed-unpaid'`.
 */
export function mapStatusToPillKey(
  status: string,
  balance_cents: number,
  paid_at: string | null | undefined,
  dispatch_time?: string | null,
  arrival_time?: string | null,
): StatusPillKey {
  void paid_at; // explicit ignore — kept in signature for caller clarity (D-25)
  const normalised = (status ?? '').toLowerCase();
  if (CANCELLED_STATUSES.has(normalised)) return 'cancelled';
  if (normalised === 'completed') return mapCompletedPillKey(balance_cents);
  if (normalised === 'late') return 'late';
  // Active lifecycle: dispatch progression (arrival/dispatch) overrides the
  // coarse pre-dispatch status. Unknown statuses fall through to `pending`.
  return mapActivePillKey(normalised, dispatch_time, arrival_time);
}

/** completed splits on balance: any positive balance → unpaid pill. */
function mapCompletedPillKey(balance_cents: number): StatusPillKey {
  const balance = typeof balance_cents === 'number' ? balance_cents : 0;
  return balance > 0 ? 'completed-unpaid' : 'completed';
}

/**
 * Pill copy (class · icon · text) per status pill key, mirroring the
 * `S` configuration in `sketches/022-appointment-detail/index.html:6436`.
 *
 * Status pill class names use the sketch's `badge-*` variants; these map
 * to Tailwind utilities via `tailwindcss-primeui` semantic tokens at the
 * render site (`StatusPill.vue`) — never raw hex codes.
 */
export const STATUS_PILL_COPY: Record<
  StatusPillKey,
  { cls: string; icon: string; text: string }
> = {
  pending: {
    cls: 'status-badge badge-info',
    icon: 'fa-clock',
    text: 'Pending',
  },
  assigned: {
    cls: 'status-badge badge-info',
    icon: 'fa-circle-check',
    text: 'Assigned',
  },
  accepted: {
    cls: 'status-badge badge-info',
    icon: 'fa-circle-check',
    text: 'Accepted',
  },
  enroute: {
    cls: 'status-badge badge-info',
    icon: 'fa-truck',
    text: 'En Route',
  },
  arrived: {
    cls: 'status-badge badge-success',
    icon: 'fa-location-dot',
    text: 'Arrived',
  },
  late: {
    cls: 'status-badge badge-warning',
    icon: 'fa-clock',
    text: 'Running Late',
  },
  completed: {
    cls: 'status-badge badge-success',
    icon: 'fa-check',
    text: 'Completed',
  },
  'completed-unpaid': {
    cls: 'status-badge badge-warning',
    icon: 'fa-circle-exclamation',
    text: 'Balance Due',
  },
  cancelled: {
    cls: 'status-badge',
    icon: 'fa-xmark',
    text: 'Cancelled',
  },
  // 5.5 D-18: mirrors the `cancelled` cls shape (no `badge-*` variant suffix)
  // for the muted/gray semantic per D-12. Icon + copy follow sketch 5.5. Key
  // quoted to match the union spelling 'expired' for explicit single-source
  // grep-ability across the file.
  expired: {
    cls: 'status-badge',
    icon: 'fa-clock',
    text: 'Expired',
  },
  // 5.2 D-21: invoice + plan-row pill copy. cls strings follow the sketch
  // `badge-*` vocabulary (mapped to Tailwind semantic tokens in StatusPill.vue).
  paid: {
    cls: 'status-badge badge-success',
    icon: 'fa-circle-check',
    text: 'Paid',
  },
  open: {
    cls: 'status-badge badge-info',
    icon: 'fa-file-invoice-dollar',
    text: 'Open',
  },
  partial: {
    cls: 'status-badge badge-warning',
    icon: 'fa-circle-half-stroke',
    text: 'Partially Paid',
  },
  // 260618-esd: net-new current-draw plan-row pill. Same warning family as
  // `partial` (amber) but distinct copy + icon — a current_draw installment is
  // fully unpaid, so "Current Draw" (fa-bullseye), never "Partially Paid". Key
  // quoted to match the union spelling 'current-draw' for grep-ability.
  'current-draw': {
    cls: 'status-badge badge-warning',
    icon: 'fa-bullseye',
    text: 'Current Draw',
  },
  'past-due': {
    cls: 'status-badge badge-danger',
    icon: 'fa-circle-exclamation',
    text: 'Past Due',
  },
  scheduled: {
    cls: 'status-badge badge-info',
    icon: 'fa-clock',
    text: 'Upcoming',
  },
};
